import {
  DateField,
  ExportButton,
  List,
  NumberField,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

import {
  Alert,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  FormProps,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useExport,
} from "@refinedev/core";

import dayjs from "dayjs";
import { IProcessedInvoice } from "interfaces";
import React from "react";

export const UkhoInvoiceReportList: React.FC<IResourceComponentsProps> = () => {
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IProcessedInvoice>({
      resource: "processed-invoice",
      syncWithLocation: false,

      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const { imoNumber, orderNo, vesselName, shipcoName, status, invoiceDate } = params;

        if (invoiceDate) {
          const startDate = invoiceDate[0].startOf("day").toISOString();
          const endDate = invoiceDate[1].endOf("day").toISOString();

          filters.push({
            field: "invoiceDate",
            operator: "gte",
            value: startDate,
          });

          filters.push({
            field: "invoiceDate",
            operator: "lte",
            value: endDate,
          });
        }

        filters.push({
          field: "imoNumber",
          operator: "contains",
          value: imoNumber ? imoNumber : undefined,
        });

        filters.push({
          field: "orderNo",
          operator: "eq",
          value: orderNo ? orderNo : undefined,
        });

        filters.push({
          field: "vesselName",
          operator: "contains",
          value: vesselName ? vesselName : undefined,
        });

        filters.push({
          field: "shipcoName",
          operator: "contains",
          value: shipcoName ? shipcoName : undefined,
        });

        filters.push({
          field: "status",
          operator: "eq",
          value: status ? status : undefined,
        });

        return filters;
      },

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
    });

  // Export uses current filters/sorters from useTable - filters update on each render
  // triggerExport is NOT memoized, so it always captures latest filter values
  const { triggerExport, isLoading: exportLoading } = useExport<IProcessedInvoice>({
    resource: "processed-invoice",
    filters: filters || [],
    sorters: sorters || [],
    filename: `ukho_invoice_report_${new Date().toISOString().slice(0, 10)}`,
    mapData: (item) => ({
      "ID": item.id,
      "Invoice No": item.invoiceNo,
      "Invoice Date": item.invoiceDate,
      "UKHO VAT No": item.ukhoVatNo,
      "Invoice Currency": item.invoiceCurrency,
      "Invoice Net Total": item.invoiceNetTotal,
      "Invoice Tax Amount": item.invoiceTaxAmount,
      "Invoice Payable Amount": item.invoicePayableAmount,
      "Order No": item.orderNo,
      "Vessel Name": item.vesselName,
      "Licence No": item.licenceNo,
      "Sub Total": item.subTotal,
      "Sub Total Pre Discount": item.subTotalPreDiscount,
      "Licence ID": item.licenceId,
      "IMO Number": item.imoNumber,
      "Shipco Name": item.shipcoName,
      "Subscription Name": item.subscriptionName,
      "Subscription Hold Status": item.subscriptionHoldStatus,
      "AVCS Subscription Type": item.avcsSubscriptionType,
      "ENP Subscription Type": item.enpSubscriptionType,
      "ADP Subscription Type": item.adpSubscriptionType,
      "ARCS Subscription Type": item.arcsSubscriptionType,
      "AVCS Discount Percentage": item.avcsDiscountPercentage,
      "ENP Discount Percentage": item.enpDiscountPercentage,
      "ADP Discount Percentage": item.adpDiscountPercentage,
      "ARCS Discount Percentage": item.arcsDiscountPercentage,
      "Order Date": item.orderDate,
      "Order Status": item.orderStatus,
      "UKHO Order ID": item.ukhoOrderId,
      "Purchase Order Number": item.purchaseOrderNumber,
      "Product Name": item.productName,
      "Product Group": item.productGroup,
      "Product ID": item.productId,
      "Invoiced Quantity": item.invoicedQuantity,
      "Purchase Order No": item.purchaseOrderNo,
      "UKHO Sales Order No": item.ukhoSalesOrderNo,
      "Validity Start Date": item.validityStartDate,
      "Validity End Date": item.validityEndDate,
      "Base Price": item.basePrice,
      "Duration Discount Percentage": item.durationDiscountPercentage,
      "Duration Discount Amount": item.durationDiscountAmount,
      "Non Duration Discount Amount": item.nonDurationDiscountAmount,
      "Admin Charge Amount": item.adminChargeAmount,
      "UKHO Net Price": item.netPrice,
      "EMR Published Price": item.emrPublishedPrice,
      "EMR Published Price After Duration Discount": item.emrPublishedPriceAfterDurationDiscount,
      "UKHO Published Price": item.ukhoPublishedPrice,
      "Tax Amount": item.taxAmount,
      "Taxable Amount": item.taxableAmount,
      "Status": item.status,
    }),
    pageSize: 10000,
  });

  function handleDateRangeSelection(value: any) {
    setDateSelected(true);
    searchFormProps?.form?.submit();
    searchFormProps?.form?.resetFields([
      "imoNumber",
      "orderNo",
      "vesselName",
      "shipcoName",
      "status",
    ]);
  }

  const columns: ColumnsType<IProcessedInvoice> = [
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      width: 140,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("invoiceNo", sorters),
      sorter: true,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 130,
      className: "wrap-text",
      render: (value) => value ? <DateField format="DD-MMM-YYYY" value={value} /> : "",
      defaultSortOrder: getDefaultSortOrder("invoiceDate", sorters),
      sorter: true,
    },
    {
      title: "Invoice Currency",
      dataIndex: "invoiceCurrency",
      key: "invoiceCurrency",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("invoiceCurrency", sorters),
      sorter: true,
    },
    {
      title: "Order No.",
      dataIndex: "orderNo",
      key: "orderNo",
      width: 100,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderNo", sorters),
      sorter: true,
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 160,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("vesselName", sorters),
      sorter: true,
    },
    {
      title: "UKHO Licence No.",
      dataIndex: "licenceNo",
      key: "licenceNo",
      width: 140,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("licenceNo", sorters),
      sorter: true,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 110,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Shipco",
      dataIndex: "shipcoName",
      key: "shipcoName",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("shipcoName", sorters),
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (value) => (
        <Tag color={value === "PROCESSED" ? "green" : value === "FAILED" ? "red" : "default"}>
          {value?.toLowerCase()}
        </Tag>
      ),
      defaultSortOrder: getDefaultSortOrder("status", sorters),
      sorter: true,
    },
    {
      title: "Product ID",
      dataIndex: "productId",
      key: "productId",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("productId", sorters),
      sorter: true,
    },
    {
      title: "Product Type",
      dataIndex: "productGroup",
      key: "productGroup",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("productGroup", sorters),
      sorter: true,
    },
    {
      title: "UKHO Published Price",
      dataIndex: "ukhoPublishedPrice",
      key: "ukhoPublishedPrice",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("ukhoPublishedPrice", sorters),
      sorter: true,
    },
    {
      title: "UKHO Net Price",
      dataIndex: "netPrice",
      key: "netPrice",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("netPrice", sorters),
      sorter: true,
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        title: "Report",
        subTitle: "UKHO Invoice",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <ExportButton
            onClick={triggerExport}
            disabled={!dateSelected}
            loading={exportLoading}
          />
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xl={24} lg={24} xs={24}>
          <Card type="inner" title={"Filter"}>
            <Filter
              formProps={searchFormProps}
              filters={filters || []}
              dateSelected={dateSelected}
              onDateRangeSelect={handleDateRangeSelection}
            />
          </Card>
        </Col>
        <Col xl={24} xs={24}>
          {dateSelected ? (
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              sticky
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          ) : (
            <Space
              direction="vertical"
              size="small"
              style={{
                display: "flex",
              }}
            >
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
              <Alert
                message="Select Invoice Date"
                description="Please select an invoice date range to view the data"
                type="info"
                showIcon
                style={{
                  margin: "auto",
                  width: "40%",
                  padding: "15px",
                  textAlign: "center",
                }}
              />
            </Space>
          )}
        </Col>
      </Row>
    </List>
  );
};

const Filter: React.FC<{
  formProps: FormProps;
  filters: CrudFilters;
  dateSelected: boolean;
  onDateRangeSelect: any;
}> = (props) => {
  const { formProps, filters, dateSelected, onDateRangeSelect } = props;

  const { RangePicker } = DatePicker;

  const invoiceDate = React.useMemo(() => {
    const start = getDefaultFilter("invoiceDate", filters, "gte");
    const end = getDefaultFilter("invoiceDate", filters, "lte");

    const startFrom = dayjs(start);
    const endAt = dayjs(end);

    if (start && end) {
      return [startFrom, endAt];
    }
    return undefined;
  }, [filters]);

  return (
    <Form
      layout="vertical"
      {...formProps}
      initialValues={{
        imoNumber: getDefaultFilter("imoNumber", filters),
        orderNo: getDefaultFilter("orderNo", filters),
        vesselName: getDefaultFilter("vesselName", filters),
        shipcoName: getDefaultFilter("shipcoName", filters),
        status: getDefaultFilter("status", filters),
        invoiceDate,
      }}
    >
      <Row gutter={[10, 0]} align="bottom">
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item
            label={"Invoice Date"}
            name="invoiceDate"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              onChange={onDateRangeSelect}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"IMO Number"} name="imoNumber">
            <Input
              placeholder="filter by IMO number"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Vessel Name"} name="vesselName">
            <Input
              placeholder="filter by vessel"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Order No."} name="orderNo">
            <Input
              placeholder="filter by order number"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Shipco"} name="shipcoName">
            <Input
              placeholder="filter by shipco"
              allowClear
              disabled={!dateSelected}
            />
          </Form.Item>
        </Col>
        <Col xl={8} md={8} sm={12} xs={24}>
          <Form.Item label={"Status"} name="status">
            <Select
              placeholder="filter by status"
              allowClear
              disabled={!dateSelected}
              options={[
                { value: "PROCESSED", label: "Processed" },
                { value: "FAILED", label: "Failed" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xl={24} md={8} sm={12} xs={24}>
          <Form.Item
            style={{
              margin: "auto",
              width: "25%",
            }}
          >
            <SaveButton
              onClick={formProps.form?.submit}
              size="large"
              block
              disabled={!dateSelected}
            >
              Filter
            </SaveButton>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
